import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import LinkWithParams from "components/LinkWithParams";
import HighlightText from "components/HighlightText";
import { ListFilterParams } from "components/typings";
import { taskBasePriceDisplay } from "pages/project/common/taskBase/taskBasePrice";
import { TaskBase } from "pages/project/common/taskBase.typing";
import { ContactCustomer } from "pages/project/common/taskBase/list/ContactCustomer";
import { TASK_LIST_TYPE, TaskListType } from "../task.typing";

export function useTaskListTable({
  filterParams,
  isUserSupplier,
  taskListType,
}: {
  filterParams: ListFilterParams<TaskBase>;
  isUserSupplier: boolean;
  taskListType: TaskListType;
}) {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<TaskBase>();

  const columns = [
    columnHelper.accessor("id", {
      header: t("task-base.fields.id"),
      cell: (info) => (
        <div className="flex items-center space-x-2">
          <LinkWithParams
            to={`./${info.getValue()}`}
            className="flex flex-row items-center whitespace-nowrap text-base font-medium underline hover:text-blue-500"
          >
            <HighlightText text={`${info.row.original.taskCode}`} term={filterParams.term} />
          </LinkWithParams>
          {info.row.original.shouldShowFeedbackIcon && (
            <ClipboardDocumentListIcon className="size-4 text-blue-500" title={t("feedback.pendingFeedback")} />
          )}
        </div>
      ),
    }),
    columnHelper.accessor("projectName", {
      size: 300,
      header: t("task-base.fields.projectName"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-projectName-value`}
        />
      ),
    }),
    columnHelper.accessor("customerName", {
      size: 200,
      header: t("task-base.fields.customer"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-customerName-value`}
        />
      ),
    }),
    ...(isUserSupplier
      ? [
          columnHelper.accessor("supplierContactName", {
            header: t("task-base.fields.supplierContact"),
            cell: (info) => (
              <HighlightText
                text={info.getValue()}
                term={filterParams.term}
                dataTestId={`${getDataTestPrefix(info)}-supplierContact`}
              />
            ),
          }),
        ]
      : []),
    columnHelper.accessor("taskType", {
      header: t("task-base.fields.type"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-taskType-value`}
        />
      ),
    }),
    columnHelper.accessor("sourceLanguage", {
      header: t("task-base.fields.source"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-sourceLanguage-value`}
        />
      ),
    }),
    columnHelper.accessor("targetLanguage", {
      header: t("task-base.fields.target"),
      cell: (info) => (
        <HighlightText
          text={info.getValue()}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-targetLanguage-value`}
        />
      ),
    }),
    columnHelper.accessor("startDate", {
      header:
        taskListType === TASK_LIST_TYPE.upcoming
          ? t("task-base.fields.estimatedStartOn")
          : t("task-base.fields.startOn"),
      cell: (info) => (
        <HighlightText
          text={
            info.getValue()
              ? t("enum:dates.mediumDateTime", { date: info.getValue() })
              : t("task-base.fields.startOnEmpty")
          }
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-startDate-value`}
        />
      ),
    }),
    columnHelper.accessor("dueDate", {
      header: t("task-base.fields.dueOn"),
      cell: (info) => (
        <HighlightText
          text={t("enum:dates.mediumDateTime", { date: info.getValue() })}
          term={filterParams.term}
          dataTestId={`${getDataTestPrefix(info)}-dueDate-value`}
        />
      ),
    }),
    columnHelper.accessor("quantity", {
      header: t("task-base.fields.quantity"),
      cell: (info) => (
        <HighlightText
          text={`${t("enum:intlNumber", { val: info.row.original.quantity })} ${info.row.original.unit}`}
          term={filterParams.term}
          className="whitespace-nowrap"
          dataTestId={`${getDataTestPrefix(info)}-quantity-value`}
        />
      ),
    }),
    ...(isUserSupplier
      ? [
          columnHelper.accessor("price", {
            header: t("task-base.fields.total"),
            cell: (info) => (
              <HighlightText
                text={taskBasePriceDisplay(info.row.original)}
                term={filterParams.term}
                className="whitespace-nowrap"
                dataTestId={`${getDataTestPrefix(info)}-total-value`}
              />
            ),
          }),
        ]
      : []),
    columnHelper.display({
      id: "actions",
      header: () => t("task-base.fields.actions"),
      cell: (info) => info.row.original.chatEnabled && <ContactCustomer task={info.row.original} />,
    }),
  ];

  return { columns };
}

function getDataTestPrefix(cellContext: CellContext<TaskBase, unknown>) {
  return `row-offer-${cellContext.row.original.id}-`;
}
